<template>
  <div class="ma-4">
    <v-card v-if="!dataLoaded">
      <v-card-title>Academic Program/Advisor Change Form (Loading...)</v-card-title>
    </v-card>
    <change-status v-else-if="'program' in submission && submission.program.length > 0 && status !== 'Returned'" :submission="submission" :term-label="termLabel" @update="(data) => submission = data"></change-status>
    <template v-else>
      <v-toolbar>
        <v-toolbar-title>Academic Program/Advisor Change: {{ termLabel }}</v-toolbar-title>
      </v-toolbar>
      <v-card class="mb-2">
        <v-card-text>
          <p>This form shows your current major(s), with advisor and any concentrations, and any minors. You can use the buttons on the bottom of each card to change something with the selected block.</p>
          <p>Please note that while you can request to change your advisor, you might be assigned a different advisor based on if the requested advisor is going on sabbatical, or to keep the department's advising duties balanced between professors, or based on a system developed by the department of the selected major.</p>
        </v-card-text>
      </v-card>
      <v-row class="my-2">
        <v-col v-for="({ major, majorDesc, advisors, concentrations, availableConcentrations, availableAdvisors, primary }, index) of current.programs" :key="major" :cols="12" :sm="6" :md="4" :xl="3">
          <v-card>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Major</v-list-item-subtitle>
                    <v-list-item-title>{{ majorDesc }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Advisor{{ advisors.length > 1 ? 's' : '' }}</v-list-item-subtitle>
                    <v-list-item-title v-for="{ text, value } in advisors" :key="'advr-' + value">{{ text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="major === 'IDS'">
                  <v-list-item-content>
                    <v-list-item-subtitle>Primary Concentration</v-list-item-subtitle>
                    <v-list-item-title v-if="!primary || primary.length === 0">Unknown</v-list-item-title>
                    <v-list-item-title v-else v-for="{ text, value } of primary" :key="'prim-conc-' + value">{{ text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="concentrations.length > 0 || availableConcentrations.length > 0">
                  <v-list-item-subtitle v-if="concentrations.length === 0">No Concentrations Selected</v-list-item-subtitle>
                  <v-list-item-content v-else>
                    <v-list-item-subtitle>Concentration{{ concentrations.length === 1 ? '' : 's' }}</v-list-item-subtitle>
                    <v-list-item-title v-for="{ text, value } of concentrations" :key="'conc-' + value">{{ text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <confirm-dialog v-if="current.programs.length > 1" title="Confirm Drop" :message="`Are you sure you want to drop your ${getDesc('major', major)} major?`" @yes="changes.push({ action: 'drop', field: 'major', value: major })">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action , field, prev }) => field === 'major' && (action === 'drop' || prev === major))">
                    <v-icon left>fal fa-minus-circle</v-icon>
                    Drop
                  </v-btn>
                </template>
              </confirm-dialog>
              <v-btn v-else :disabled="!!changes.find(({ field }) => field === 'major')" class="mb-1" @click="openMajorChangeDialog(major === '0000' ? 'change' : 'add', major === '0000' ? major : null)">
                <v-icon left>fal fa-plus-circle</v-icon>
                {{ major === '0000' ? 'Declare major' : 'Add' }}
              </v-btn>
              <v-spacer />
              <!-- <v-menu v-if="major !== '0000'" v-model="changeMajorMenu[index]" :close-on-content-click="true">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field, prev }) => action === 'change' && field === 'major' && prev === major)">
                    <v-icon left>fal fa-exchange</v-icon>
                    Change
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="openMajorChangeDialog('change', major)">
                    <v-list-item-title>Change Major</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="availableAdvisors.filter(({value}) => !advisors.find(({ value: v }) => v === value)).length === 0" @click="openAdvisorChangeDialog(major)">
                    <v-list-item-content>
                      <v-list-item-title>Request Advisor Change</v-list-item-title>
                      <v-list-item-subtitle v-if="availableAdvisors.filter(({value}) => !advisors.find(({ value: v }) => v === value)).length === 0">No other advisors available</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <conc-dialog v-if="availableConcentrations.length > 0" :major="major" :current-concentrations="concentrations" :primary-concentrations="primary" @save="(c) => updateConcentrations(index, c)">
                    <template v-slot:activator="{ on }">
                      <v-list-item v-on="on" :disabled="!!changes.find(({ major: m, field }) => major === m && ['major', 'concentration'].includes(field))" @click.stop="changeMajorMenu[index] = false">
                        <v-list-item-title>Change Concentrations</v-list-item-title>
                      </v-list-item>
                    </template>
                  </conc-dialog>
                </v-list>
              </v-menu> -->
              <change-menu v-if="major !== '0000'"
                :major="major"
                :available-advisors="availableAdvisors"
                :current-advisors="advisors"
                :available-concentrations="availableConcentrations"
                :current-concentrations="concentrations"
                :primary-concentrations="primary || []"
                :changes="changes"
                @major="openMajorChangeDialog('change', major)"
                @advisor="openAdvisorChangeDialog(major, advisors)"
                @concentration="(c) => updateConcentrations(index, c)" />
            </v-card-actions>
          </v-card>
        </v-col>
        <major-dialog v-model="majorDialogActive" :action="majorDialogAction" :major-items="majorItems" @save="saveMajorChange" />
        <advisor-dialog v-model="advisorDialogActive" :prev-adv="advisorChangePrevious" :major="advisorChangeMajor" @save="saveAdvisorChange" />
        <v-col :cols="12" :sm="6" :md="4" :xl="3">
          <v-card>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Minor{{ current.minors.length === 1 ? '' : 's' }}</v-list-item-subtitle>
                    <v-list-item-title v-for="{ value, text } in current.minors" :key="'minor-' + value">{{ text }}</v-list-item-title>
                    <v-list-item-title v-if="current.minors.length === 0">**No declared minors**</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-dialog v-if="current.minors.length < 3" v-model="addMinorDialog" width="500" persistent>
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field }) => action === 'add' && field === 'minor')">
                    <v-icon left>fal fa-plus-circle</v-icon>
                    Add
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title>Add Minor(s)</v-card-title>
                  <v-card-text>
                    <p>Please choose what minor you would like to add. You can have 3 total minors.</p>
                    <v-autocomplete v-model="minorsToAdd" :items="minorAddOptions" label="Minor(s)" multiple outlined chips small-chips deletable-chips />
                  </v-card-text>
                  <v-card-actions>
                    <v-btn text @click="addMinorDialog = false">Cancel</v-btn>
                    <v-spacer />
                    <v-btn :disabled="minorsToAdd.length === 0" color="success" @click="addMinors()">Save</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <v-spacer />
              <confirm-dialog v-if="current.minors.length === 1" title="Confirm Drop" :message="`Are you sure you want to drop your ${current.minors[0].text} minor?`" @yes="changes.push({ action: 'drop', field: 'minor', value: current.minors[0].value })">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field }) => action === 'drop' && field === 'minor')">
                    <v-icon left>fal fa-minus-circle</v-icon>
                    Drop
                  </v-btn>
                </template>
              </confirm-dialog>
              <v-menu v-else-if="current.minors.length > 1" v-model="minorDrop">
                <template #activator="{ on }">
                  <v-btn v-on="on" :disabled="!!changes.find(({ action, field}) => action === 'drop' && field === 'minor')">
                    <v-icon left>fal fa-minus-circle</v-icon>
                    Drop
                  </v-btn>
                </template>
                <v-list>
                  <confirm-dialog v-for="{ value, text } in current.minors" :key="value" title="Confirm Drop" :message="`Are you sure you want to drop your ${text} minor?`" @yes="changes.push({ action: 'drop', field: 'minor', value })">
                    <template #activator="{ on }">
                      <v-list-item v-on="on" :disabled="changes.find(({ action, field }) => action === 'drop' && field === 'minor')">
                        <v-list-item-title>Drop {{ text }}</v-list-item-title>
                      </v-list-item>
                    </template>
                  </confirm-dialog>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-card v-if="includeP12 || isEDSTMajor" class="mb-2">
        <v-card-text>
          <h4>Education or Teacher Certification</h4>
          <template v-if="includeP12">
            <p>Students who desire to teach in a field from their selected major are encouraged to minor in Education followed by completion of the Master of Arts in Teaching degree at Covenant. Note: if you choose not to minor in Education, you are required to complete three pre-requisite education courses: Introduction to Teaching, Education Psychology, and Education of Exceptional Children.</p>
            <v-checkbox :value="P12Secondary" label="P-12 or Secondary (grades 6-12) Education - the five year secondary certification program including MAT degree" readonly />
          </template>
          <p v-if="isEDSTMajor">
            Students who desire middle grades certification should complete the fifth-year Master of Arts in Teaching degree at Covenant. You must also select two content fields in the concentration selection with your major above.
          </p>
        </v-card-text>
      </v-card>
      <v-alert v-for="({ action, field, value, prev, advisorName, concentrations, changeText }, index) of myChanges" :key="action + '-' + field + '-' + value" :color="action === 'add' ? 'success' : (action === 'drop' ? 'error' : 'info')" colored-border border="left">
        <template #prepend>
          <v-icon v-if="action === 'add'" class="mr-4">fas fa-plus-circle</v-icon>
          <v-icon v-else-if="action === 'drop'" class="mr-4">fas fa-minus-circle</v-icon>
          <v-icon v-else class="mr-4">fas fa-exchange</v-icon>
        </template>
        {{ changeText }}
        <template #append>
          <v-btn @click="changes.splice(index, 1)">Cancel</v-btn>
        </template>
      </v-alert>
      <v-card v-if="changes.length > 0">
        <v-card-text>
          <v-textarea v-model="comment" label="Add Comment" rows="3" outlined hide-details />
        </v-card-text>
        <!-- <v-card-actions>
          <v-btn :disabled="!comment" color="success" text @click="addComment">Save Comment</v-btn>
        </v-card-actions> -->
      </v-card>
      <v-toolbar flat style="margin-bottom:40px" class="mt-4">
        <v-btn text to="/student" class="mr-2">Cancel</v-btn>
        <v-btn color="success" :disabled="!isValid || isSubmitting" @click="save">{{ isSubmitting ? 'Submitting' : 'Submit' }}</v-btn>
        <v-spacer></v-spacer>
      </v-toolbar>
    </template>
  </div>
</template>
<script>
import { ref, computed, reactive, watch, onMounted } from '@vue/composition-api'

export default {
  components: {
    ConfirmDialog: () => import('@/components/system/confirmDialog'),
    MajorDialog: () => import('@/components/student/program-change/major-dialog'),
    AdvisorDialog: () => import('@/components/student/program-change/advisor-dialog'),
    ChangeMenu: () => import('@/components/student/program-change/change-menu')
    // ConcDialog: () => import('@/components/student/program-change/conc-dialog')
  },
  setup (props, { root }) {
    const service = root.$feathers.service('student/major-change')
    const settingServ = root.$feathers.service('student/reg-setting')

    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const roles = computed(() => root.$store.state.roles)
    const required = ref([(v) => !!v || 'Required'])
    const dataLoaded = ref(false)
    const term = ref('')
    const termLabel = ref('')

    const current = reactive({
      programs: [],
      minors: [],
      p12: false,
      contentField: []
    })
    const isResubmit = ref(false)
    const changes = ref([])
    const myChanges = ref([])
    watch(changes, async () => {
      const arr = []
      for (const rec of changes.value) {
        const { action, field, value, prev, advisor, advisorName, concentrations } = rec
        let changeText = action.substring(0, 1).toUpperCase() + action.substring(1) + ' ' + field.substring(0, 1).toUpperCase() + field.substring(1)
        if (action === 'change') {
          changeText += ` from ${field === 'advisor' ? await getAdvrName(prev) : await getDesc(field, prev)} to`
        }
        changeText += ': ' + (field === 'advisor' ? advisorName : await getDesc(field, value))
        if (field === 'major') {
          changeText += ' with '
          if (advisorName) changeText += ' Advisor: ' + advisorName
          else changeText += ' Advisor: ' + await getAdvrName(advisor)
          if (concentrations && concentrations.length > 0) {
            if (advisorName) changeText += ' and'
            const temp = []
            for (const conc of concentrations) {
              temp.push(await getDesc('concentration', conc))
            }
            changeText += ' Concentrations: ' + temp.join('; ')
          }
        }
        arr.push({ ...rec, changeText })
      }
      myChanges.value = arr
    })

    const submission = ref({})
    const status = computed(() => 'status' in submission.value ? submission.value.status : 'Unsubmitted')
    const original = reactive({})

    const majorItems = ref([])

    const minor = ref([])
    const minorItems = ref([])
    watch(minor, () => {
      if (minor.value.length > 2) {
        minor.value.splice(2, 1)
      }
    })

    onMounted(async () => {
      if ('Records' in roles.value || 'Employee' in roles.value || 'All Adjuncts' in roles.value || 'Technology Services' in roles.value) {
        root.$router.replace('/student/program-change/admin')
        return
      }
      // Get possible terms from the term warehouse; all terms that have not yet ended
      const { data: termData } = await root.$feathers.service('system/term').find({ query: { end: { $gt: new Date() }, $limit: 50 } })
      const terms = termData.map(({ term }) => term)
      // Load the PIDM of the current user
      const pidm = user.value.pidm
      // Check to see if the student has a submission for any of the given terms; if so, redirect to the status page
      const { data: changeData } = await service.find({ query: { pidm, term: { $in: terms } } })
      if (changeData.length > 0) {
        if (changeData[0].status === 'Returned') {
          changes.value = changeData[0].changes
          isResubmit.value = true
        } else {
          root.$router.replace('/student/program-change/status')
          return
        }
      }
      // Load the major options
      settingServ.find({ query: { form: 'program', category: 'major', $sort: { term: -1 }, $limit: 1 } }).then(({ data }) => {
        majorItems.value = data[0].options
      })
      // Load the minor options
      settingServ.find({ query: { form: 'program', category: 'minor', $sort: { term: -1 }, $limit: 1 } }).then(({ data }) => {
        minorItems.value = data[0].options
      })
      // Load the student's academic data from the student/term-detail for any of the active or future terms, sorted with the term code ascending
      // This will load the current term first, if there is a record for it, or for the next term to begin
      const { data: studentData } = await root.$feathers.service('student/term-detail').find({ query: { pidm, term: { $in: terms }, $sort: { term: 1 } } })
      if (studentData.length > 0) {
        let { academics, term: termCode } = studentData[0]
        term.value = termCode
        const { data: termData } = await root.$feathers.service('system/term').find({ query: { term: termCode } })
        if (termData.length > 0) termLabel.value = termData[0].label
        for (let l in academics) {
          if (Array.isArray(academics[l])) original[l] = academics[l].filter((val) => val !== '')
          else original[l] = academics[l]
        }
        setInitialData(academics)
        dataLoaded.value = true
      } else {
        root.$store.dispatch('main/snackbar', { active: true, color: 'error', timeout: 10000, text: 'No enrollment details were found for you in any active term. Please contact the Records office for assistance.' })
        root.$router.replace('/')
      }
    })

    async function setInitialData ({ major, minor, concentration, advisor }) {
      current.programs = []

      let isFirst = true
      for (const maj of major) {
        const majorDesc = await getDesc('major', maj)
        const prog = {
          major: maj,
          majorDesc,
          advisors: [],
          concentrations: [],
          availableAdvisors: [],
          availableConcentrations: []
        }
        const { data } = await settingServ.find({ query: { form: 'program', major: maj, $sort: { category: 1, term: -1 } } })
        for (const { category, options } of data) {
          if (category === 'advisor') {
            // Find any major advisors in the advisor list and match them to what's in the options list
            prog.availableAdvisors = options
            const arr = options.filter(({ value }) => advisor.find(({ code, pidm }) => code.substr(0, 3) === 'MAJ' && pidm === value))
            if (arr.length === 0) {
              const advrPidm = advisor.find(({ code }) => code === (isFirst ? 'MAJR' : 'MAJ2')).pidm
              const text = await getAdvrName(advrPidm)
              prog.advisors = [{ value: advrPidm, text }]
            } else {
              prog.advisors = arr
            }
          } else if (category === 'concentration') {
            prog.concentrations = options.filter(({ value }) => concentration.includes(value))
            prog.availableConcentrations = options
          } else if (category === 'primary-concentration') {
            prog.primary = options.filter(({ value }) => concentration.includes(value))
          }
        }
        current.programs.push(prog)
        isFirst = false
      }
      current.minors = minorItems.value.filter(({ value }) => minor.includes(value))
    }

    const isSubmitting = ref(false)
    async function save () {
      isSubmitting.value = true
      const obj = {
        term: term.value,
        pidm: user.value.pidm,
        bannerId: user.value.bannerId,
        name: user.value.name,
        status: 'Submitted',
        changes: changes.value
      }
      const timeline = [{
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: isResubmit.value ? 'Request Re-Submitted' : 'Request Submitted',
        icon: 'fal fa-save',
        color: 'info',
        visibleToStudent: true
      }]
      if (comment.value) {
        timeline.push({
          pidm: user.value.pidm,
          name: user.value.name,
          date: new Date(),
          text: comment.value,
          icon: 'fal fa-comment',
          color: 'info',
          visibleToStudent: true
        })
      }
      let { data } = await service.find({ query: { term: term.value, pidm: user.value.pidm } })
      if (data.length > 0) {
        obj.$push = { timeline }
        await service.patch(data[0]._id, obj)
      } else {
        obj.timeline = timeline
        await service.create(obj)
      }
      await root.$feathers.service('system/email').create({
        from: { email: 'report-email@covenant.edu', name: 'Program Change' },
        to: 'recordsoffice@covenant.edu',
        subject: 'Program/Advisor Change Submitted',
        html: user.value.preferred + ' ' + user.value.last + ' has submitted a program/advisor change. Please click on the link below to access the Program/Advisor Change system to review this request.<br/><br/><a href="https://portal.covenant.edu/student/program-change">https://portal.covenant.edu/student/program-change</a>'
      })
      root.$store.dispatch('main/snackbar', { active: true, color: 'success', timeout: 6000, text: 'Your program/advisor change form was submitted successfully.' })
      root.$router.push('/student/program-change/status')
      isSubmitting.value = false
    }

    /** Education add-ons */
    const eduMajors = ['ART', 'BIB', 'BIO', 'CHE', 'COS', 'ECO', 'ENG', 'FRE', 'GER', 'HIS', 'MAT', 'MUS', 'PHY', 'POLS', 'SPA', 'THET']
    const includeP12 = computed(() => {
      // Check to see if they have one of the EDU majors, and if they are not dropping that major
      // Or check to see if they are adding or changing to one of the EDU majors
      if (current.programs.find(({ major }) => eduMajors.includes(major) && !changes.value.find(({ field, value }) => field === 'major' && value === major))) {
        return true
      } else if (changes.value.find(({ action, field, value }) => action !== 'drop' && field === 'major' && eduMajors.includes(value))) {
        return true
      }
      return false
    })
    const P12Secondary = ref(false)
    watch(P12Secondary, (val) => {
      const changeMinor = changes.value.find(({ field, value }) => field === 'major' && value === 'EDU')
      if (val && (!changeMinor || changeMinor.action !== 'drop') && !minor.value.includes('EDU')) {
        changes.value.push({ action: 'add', field: 'minor', value: 'EDU' })
      }
    }, {
      lazy: true
    })
    watch(changes, () => {
      const changeMinor = changes.value.find(({ field, value }) => field === 'major' && value === 'EDU')
      if (P12Secondary.value && changeMinor.action === 'drop') {
        P12Secondary.value = false
      }
    })

    const isEDSTMajor = computed(() => {
      const changeRec = changes.value.find(({ field, value }) => field === 'major' && value === 'EDST')
      if (current.programs.find(({ major }) => major === 'EDST') && (!changeRec || changeRec.action !== 'drop')) {
        return true
      } else {
        return !!changeRec
      }
    })
    const EDSTContentFields = ref([])
    const contentFieldItems = ref([
      { value: 'EDLA', text: 'Language Arts' },
      { value: 'EDMA', text: 'Mathematics' },
      { value: 'EDSC', text: 'Science' },
      { value: 'EDSS', text: 'Social Studies' }
    ])
    watch(EDSTContentFields, () => {
      if (EDSTContentFields.value.length > 2) {
        EDSTContentFields.value.splice(2, 1)
      }
    })

    const isValid = computed(() => changes.value.length > 0)

    const form = ref(null)
    const formValid = ref(false)

    async function getAdvrName (pidm) {
      const { data: dirData } = await root.$feathers.service('directory/people').find({ query: { pidm } })
      if (dirData.length > 0) {
        return dirData[0].name.last + ', ' + dirData[0].name.first
      }
      return 'Advisor Not Found'
    }
    async function getDesc (category, value) {
      let arr = []
      if (category === 'major') {
        arr = majorItems.value
      } else if (category === 'minor') {
        arr = minorItems.value
      } else if (category === 'advisor') {
        for (const { advisors, availableAdvisors } of current.programs) {
          if (Array.isArray(value)) {
            const res = advisors.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            if (res.length > 0) return res.map(({ text }) => text).join('; ')
            const rec = availableAdvisors.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            if (rec) return rec.map(({ text }) => text).join('; ')
            return lookupValue(category, value)
          } else {
            const res = advisors.find(({ value: v }) => value === v)
            if (res) return res.text
            const rec = availableAdvisors.find(({ value: v }) => value === v)
            if (rec) return rec.text
            return lookupValue(category, value)
          }
        }
      } else if (category === 'concentration') {
        for (const { concentrations, availableConcentrations } of current.programs) {
          if (Array.isArray(value)) {
            // const res = concentrations.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            // if (res.length > 0) return res.map(({ text }) => text).join('; ')
            // const rec = availableConcentrations.filter(({ value: v }) => !!value.find(({ value }) => value === v))
            // if (rec) return rec.map(({ text }) => text).join('; ')
            if (typeof (value[0]) === 'object') return lookupValue(category, value.map(({ value }) => value))
            else return lookupValue(category, value)
          } else {
            const res = concentrations.find(({ value: v }) => value === v)
            if (res) {
              return res.text
            }
            const rec = availableConcentrations.find(({ value: v }) => value === v)
            if (rec) {
              return rec.text
            }
            return lookupValue(category, value)
          }
        }
      }
      if (arr) {
        if (Array.isArray(value)) {
          return arr.filter(({ value: v }) => value.includes(v)).map(({ text }) => text).join('; ')
        } else {
          const rec = arr.find(({ value: v }) => value === v)
          if (rec) return rec.text
        }
      }
      return value
    }
    async function lookupValue (category, value) {
      const aggregate = [
        { $match: {
          form: 'program',
          category
        } },
        { $unwind: '$options' },
        { $match: {
          'options.value': Array.isArray(value) ? { $in: value } : value
        } },
        { $project: {
          value: '$options.value',
          text: '$options.text'
        } }
      ]
      if (category === 'concentration') aggregate[0].$match.category = { $in: ['concentration', 'primary-concentration'] }
      const { data } = await settingServ.find({ query: { aggregate } })
      if (data.length > 1) return data.map(({ text }) => text).join('; ')
      else if (data.length === 1) return data[0].text
    }

    const majorDialogActive = ref(false)
    const majorDialogAction = ref('')
    const majorDialogMajor = ref('')
    function openMajorChangeDialog (action, major) {
      changeMajorMenu.value = [false, false]
      majorDialogAction.value = action
      majorDialogMajor.value = major
      majorDialogActive.value = true
    }
    async function saveMajorChange (rec) {
      changeMajorMenu.value = [false, false]
      const advisorName = await getAdvrName(rec.advisor)
      changes.value.push({ action: majorDialogAction.value, field: 'major', prev: majorDialogMajor.value, ...rec, advisorName })
    }

    const advisorDialogActive = ref(false)
    const advisorChangeMajor = ref('')
    const advisorChangePrevious = ref([])
    function openAdvisorChangeDialog (major, advisors) {
      changeMajorMenu.value = [false, false]
      advisorChangeMajor.value = major
      advisorChangePrevious.value = advisors
      advisorDialogActive.value = true
    }
    async function saveAdvisorChange (rec) {
      changeMajorMenu.value = [false, false]
      const advisorName = await getAdvrName(rec.value)
      changes.value.push({ action: 'change', field: 'advisor', ...rec, advisorName })
    }

    const minorAddOptions = computed(() => minorItems.value.filter(({ value }) => !current.minors.find(({ value: v }) => value === v)))
    const addMinorDialog = ref(false)
    const minorsToAdd = ref([])
    async function addMinors () {
      for (const minor of minorsToAdd.value) {
        changes.value.push({ action: 'add', field: 'minor', value: minor })
      }
      addMinorDialog.value = false
    }

    watch(minorsToAdd, () => {
      if (minorsToAdd.value.length > 0) {
        let i = current.minors.length - changes.value.filter(({ action, field }) => action === 'drop' && field === 'minor').length
        if (i === 3) minorsToAdd.value = []
        if (minorsToAdd.value.length + i > 3) {
          minorsToAdd.value.splice(3 - i, 3)
        }
      }
    })

    const comment = ref('')
    async function addComment () {}

    const changeMajorMenu = ref([false, false])
    function closeMenu (index) {
      changeMajorMenu.value.splice(index, 1, false)
      return true
    }

    function updateConcentrations (index, concentrations) {
      if (current.programs[index].concentrations.length === 0) {
        changes.value.push({ action: 'add', field: 'concentration', major: current.programs[index].major, value: concentrations })
      } else if (concentrations.length === 0) {
        changes.value.push({ action: 'drop', field: 'concentration', major: current.programs[index].major, value: current.programs[index].concentrations })
      } else {
        const arr = [ ...current.programs[index].concentrations ]
        if (current.programs[index].primary) arr.splice(0, 0, current.programs[index].primary[0])
        // Get the differences between the prev and passed concentrations
        const prev = arr.filter(({ value }) => !concentrations.includes(value)).map(({ value }) => value)
        const value = concentrations.filter((val) => !arr.find(({ value }) => value === val))
        if (prev.length === 0 && value.length === 0) {
          root.$store.dispatch('main/snackbar', { color: 'error', text: 'No changes were made to your concentrations.' })
        } else {
          changes.value.push({ action: 'change', field: 'concentration', major: current.programs[index].major, prev, value })
        }
      }
    }

    return {
      user,
      roles,
      required,
      dataLoaded,
      term,
      termLabel,
      current,
      changes,
      myChanges,
      submission,
      status,
      majorItems,
      minor,
      minorItems,
      setInitialData,
      isSubmitting,
      save,
      includeP12,
      P12Secondary,
      isEDSTMajor,
      EDSTContentFields,
      contentFieldItems,
      isValid,
      form,
      formValid,
      getAdvrName,
      getDesc,
      majorDialogActive,
      majorDialogAction,
      majorDialogMajor,
      openMajorChangeDialog,
      saveMajorChange,
      advisorDialogActive,
      advisorChangeMajor,
      advisorChangePrevious,
      openAdvisorChangeDialog,
      saveAdvisorChange,
      minorAddOptions,
      addMinorDialog,
      minorsToAdd,
      addMinors,
      comment,
      addComment,
      changeMajorMenu,
      closeMenu,
      updateConcentrations
    }
  }
}
</script>
